/**
 * Intervalul de livrare promis si, dupa livrare, cel realizat.
 *
 * Masurarea promis-vs-realizat porneste din prima zi (Etapa 1 din plan): fara
 * ea nu exista baza pe care sa se sprijine rutarea inteligenta de mai tarziu.
 * De aceea ecranul o arata clientului, nu doar o trimite in telemetrie.
 *
 * `confidence` vine de la core. Cand estimarea e din istoric (POS-ul a raspuns
 * 429 la interogarea de backlog, ADR-009), se spune — un interval prezentat cu
 * aceeasi siguranta ca unul masurat e o promisiune pe care nu o sustine nimic.
 */

import type { EtaWindowDto } from "@deliveryapp/shared";

const CONFIDENCE_NOTES: Record<EtaWindowDto["confidence"], string | null> = {
  high: null,
  low: "Interval orientativ: bucătăria are un volum neobișnuit acum.",
  cached: "Interval estimat din livrările anterioare, nu din starea de acum a bucătăriei.",
};

export function formatClock(iso: string | null | undefined, timezone: string): string {
  if (typeof iso !== "string" || iso.length === 0) return "—";
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: timezone,
    }).format(value);
  } catch {
    // Fus orar necunoscut in configul pietei: ora locala a browserului e o
    // aproximare onesta, spre deosebire de a nu afisa nimic.
    return new Intl.DateTimeFormat("ro-RO", { hour: "2-digit", minute: "2-digit" }).format(value);
  }
}

function minutesBetween(from: string, to: string): number | null {
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 60_000);
}

export function EtaWindow({
  eta,
  timezone,
  deliveredAt,
  degraded = false,
}: {
  eta: EtaWindowDto;
  timezone: string;
  deliveredAt?: string | null;
  degraded?: boolean;
}) {
  const note = CONFIDENCE_NOTES[eta.confidence];
  const from = formatClock(eta.deliveryFrom, timezone);
  const to = formatClock(eta.deliveryTo, timezone);

  let realized: string | null = null;
  if (typeof deliveredAt === "string" && deliveredAt.length > 0) {
    const late = minutesBetween(eta.deliveryTo, deliveredAt);
    const early = minutesBetween(deliveredAt, eta.deliveryFrom);
    const clock = formatClock(deliveredAt, timezone);
    if (late !== null && late > 0) {
      realized = `Livrat la ${clock}, cu ${late} ${late === 1 ? "minut" : "minute"} peste intervalul promis.`;
    } else if (early !== null && early > 0) {
      realized = `Livrat la ${clock}, cu ${early} ${early === 1 ? "minut" : "minute"} înainte de interval.`;
    } else {
      realized = `Livrat la ${clock}, în intervalul promis.`;
    }
  }

  return (
    <section className="rounded-card border border-line bg-surface p-4" aria-labelledby="eta-titlu">
      <h2 id="eta-titlu" className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
        Livrare estimată
      </h2>
      <p className="mt-1 text-2xl font-bold text-ink">
        {from} – {to}
      </p>
      {note === null ? null : <p className="mt-1 text-sm text-ink-muted">{note}</p>}
      {degraded ? (
        <p className="mt-1 text-sm text-ink-muted">
          Estimarea nu a putut fi actualizată din bucătărie chiar acum.
        </p>
      ) : null}
      {realized === null ? null : <p className="mt-2 text-sm font-medium text-ink">{realized}</p>}
    </section>
  );
}
